(ns hai.ui.components.collection.views.kanban-core
  "Pure kanban grouping/drag core: JS-free functions the kanban view builds on.

  Extracted from `hai.ui.components.collection.views.kanban` (HAI-001-14) so the
  grouping/drag decisions can be characterized in plain CLJ, with no react /
  @dnd-kit / ornament / `hai.ui.components` dependencies.

  Two public fns:

  - `group-lanes` - the pure grouping core, a function of the collection map.
    REPL-inspectable, no rendering.
  - `move-decision` (HAI-003-02) - the drag analogue: decides whether a drop is
    a lane change or a same-lane no-op.

  The view ns (`.cljs`) requires this ns and calls these two fns; every
  rendering, drag, and ornament form stays in the view.")

(defn- lane-assign-fn
  "The row -> lane-id function for a collection.

  Uses `:collection.view.kanban/bucket-fn` when present (the general mapping,
  e.g. the tasks board's `task-bucket`); otherwise falls back to value-equality
  on the `:collection.view.kanban/group-by` attr. Rows are raw entities at this
  seam (the collection field's `:form.field/current-value` holds unwrapped
  maps), so both paths read entity attrs directly."
  [{:collection.view.kanban/keys [bucket-fn] group-attr :collection.view.kanban/group-by}]
  (or bucket-fn #(get % group-attr)))

(defn- catch-all-lane-id
  "Id of the lane flagged `:collection.view.kanban.lane/catch-all?`, if any.

  Optional: when a lane is so flagged, rows whose assigned lane-id matches no
  declared lane (including nil) collapse into it. With no flagged lane, such
  rows are dropped rather than dumped into an arbitrary lane."
  [lanes]
  (some (fn [{:collection.view.kanban.lane/keys [id catch-all?]}]
          (when catch-all? id))
        lanes))

(defn group-lanes
  "Group a collection's rows into ordered lanes.

  Reads rows from the collection form field
  (`:table/form` -> `:form/fields` -> `:table/rows` -> `:form.field/current-value`)
  and the `:collection.view.kanban/*` config off the collection. Pure: a
  function of the collection map, with no rendering.

  Returns each declared lane as-is with `:collection.view.kanban.lane/cards`
  assoc'd on, in declared lane order. Every declared
  lane is present, including empty ones (`:collection.view.kanban.lane/cards []`).
  Lane assignment is `bucket-fn` when supplied, else value-equality on the
  `group-by` attr. Rows whose assigned id matches no declared lane (including
  nil) collapse into the `:collection.view.kanban.lane/catch-all?` lane when one
  is declared, and are otherwise omitted."
  [{:collection.view.kanban/keys [lanes] :as collection}]
  (let [rows      (-> collection :table/form :form/fields :table/rows
                      :form.field/current-value)
        assign    (lane-assign-fn collection)
        lane-ids  (into #{} (map :collection.view.kanban.lane/id) lanes)
        catch-all (catch-all-lane-id lanes)
        by-lane   (group-by (fn [row]
                              (let [id (assign row)]
                                (cond
                                  (contains? lane-ids id) id
                                  catch-all               catch-all
                                  :else                   ::unmatched)))
                            rows)]
    ;; The declared lane is carried through rather than rebuilt from its id and
    ;; label: whatever else it declares — a background, say — is the view's to
    ;; read, and rebuilding would quietly drop it.
    (mapv (fn [{:collection.view.kanban.lane/keys [id] :as lane}]
            (assoc lane :collection.view.kanban.lane/cards (vec (get by-lane id []))))
          lanes)))

(defn move-decision
  "Decide whether dropping a card from one lane onto another is a move.

  Pure: a function of the two lane-ids, the drag analogue of `group-lanes`. When
  the card lands on a different lane, returns `{:from-lane-id :to-lane-id}`; when
  it lands back on its own lane, returns `nil` (the same-lane no-op). Rendering,
  re-frame, and the actual write live elsewhere - this only names the decision so
  it is REPL-inspectable without a browser."
  [from-lane-id to-lane-id]
  (when (not= from-lane-id to-lane-id)
    {:from-lane-id from-lane-id
     :to-lane-id   to-lane-id}))
